import { useTranslation } from "react-i18next";
import { storage } from "@/lib/local-storage";
import type { ContainerStatus, ContainerPhysicalStatus, DocumentStatus, DocumentType, RiskLevel } from "@/types/container";

export const useI18n = () => {
    const { t, i18n } = useTranslation();

    // Helper functions for translating types/enums
    const translateDocumentStatus = (status: DocumentStatus): string => {
        const statusMap: Record<DocumentStatus, string> = {
            Completo: "documentStatus.complete",
            Pending: "documentStatus.pending",
            Error: "documentStatus.error",
        };
        return t(statusMap[status] || status, { ns: "types" });
    };

    const translateContainerStatus = (status: ContainerStatus): string => {
        const statusMap: Record<ContainerStatus, string> = {
            "Draft": "containerStatus.draft",
            "Booked": "containerStatus.booked",
            "In Transit": "containerStatus.inTransit",
            "Arrived": "containerStatus.arrived",
            "Delivered": "containerStatus.delivered",
            "On Hold": "containerStatus.onHold",
            "Cancelled": "containerStatus.cancelled",
            "Delayed": "containerStatus.delayed",
            "At Port": "containerStatus.atPort",
            "Pending": "containerStatus.pending",
            "Booking Confirmed": "containerStatus.bookingConfirmed",
            "Documents Pending": "containerStatus.documentsPending",
            "Ready to Ship": "containerStatus.readyToShip",
            "Customs Clearance": "containerStatus.customsClearance",
            "Customs Cleared": "containerStatus.customsCleared",
            "Out for Delivery": "containerStatus.outForDelivery",
            "Completed": "containerStatus.completed",
        };
        return t(statusMap[status] || status, { ns: "types" });
    };

    const translatePhysicalStatus = (status: ContainerPhysicalStatus): string => {
        const statusMap: Record<ContainerPhysicalStatus, string> = {
            "PENDING": "containerPhysicalStatus.PENDING",
            "GATE_OUT_EMPTY": "containerPhysicalStatus.GATE_OUT_EMPTY",
            "LOADING": "containerPhysicalStatus.LOADING",
            "GATE_IN": "containerPhysicalStatus.GATE_IN",
            "LOAD_ON_VESSEL": "containerPhysicalStatus.LOAD_ON_VESSEL",
            "VESSEL_DEPARTED": "containerPhysicalStatus.VESSEL_DEPARTED",
            "IN_TRANSIT": "containerPhysicalStatus.IN_TRANSIT",
            "VESSEL_ARRIVED": "containerPhysicalStatus.VESSEL_ARRIVED",
            "DISCHARGED": "containerPhysicalStatus.DISCHARGED",
            "GATE_OUT_DELIVERY": "containerPhysicalStatus.GATE_OUT_DELIVERY",
            "DELIVERED": "containerPhysicalStatus.DELIVERED",
            "ON_HOLD": "containerPhysicalStatus.ON_HOLD",
            "CANCELLED": "containerPhysicalStatus.CANCELLED",
        };
        return t(statusMap[status] || status, { ns: "types" });
    };

    const translateRiskLevel = (level: RiskLevel): string => {
        const levelMap: Record<RiskLevel, string> = {
            Low: "riskLevel.low",
            Medium: "riskLevel.medium",
            High: "riskLevel.high",
            Critical: "riskLevel.critical",
        };
        return t(levelMap[level] || level, { ns: "types" });
    };

    const translateDocumentType = (type: DocumentType): string => {
        const typeMap: Record<DocumentType, string> = {
            bill_of_lading: "documentType.billOfLading",
            invoice: "documentType.invoice",
            phytosanitary_certificate: "documentType.phytosanitaryCertificate",
            movement_certificate: "documentType.movementCertificate",
            packing_list: "documentType.packingList",
            fumigation_certificate: "documentType.fumigationCertificate",
            sad_icm: "documentType.sadIcm",
            coo: "documentType.coo",
            unknown: "documentType.unknown",
        };
        return t(typeMap[type] || type, { ns: "types" });
    };

    const translateRole = (role: string): string => {
        const roleMap: Record<string, string> = {
            "Shipper": "roles.shipper",
            "Consignee": "roles.consignee",
            "Receiver": "roles.receiver",
            "Notify Party": "roles.notifyParty",
            "Freight Forwarder": "roles.freightForwarder",
            "Forwarder": "roles.forwarder",
            "Carrier": "roles.carrier",
            "Ship Owner": "roles.shipOwner",
            "Land Transport": "roles.landTransport",
            "Customs Broker": "roles.customsBroker",
            "Port of Loading": "roles.portOfLoading",
            "Port of Discharge": "roles.portOfDischarge",
            "Airport Staff": "roles.airportStaff",
            "Authority": "roles.authority",
            "Insurance Provider": "roles.insuranceProvider",
            "Inspection Agency": "roles.inspectionAgency",
            "Sales": "roles.sales",
            "Supplier": "roles.supplier",
        };
        return roleMap[role] ? t(roleMap[role], { ns: "types" }) : role;
    };

    const changeLanguage = (lang: "en" | "it" | "es") => {
        i18n.changeLanguage(lang);
        storage.setItem("i18nextLng", lang);
    };

    const currentLanguage = i18n.language || "en";

    return {
        t,
        i18n,
        changeLanguage,
        currentLanguage,
        translateDocumentStatus,
        translateContainerStatus,
        translatePhysicalStatus,
        translateRiskLevel,
        translateDocumentType,
        translateRole,
    };
};
